'use client'

import Icon from '@/shared/v2/shared/components/Icon'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import * as Toast from '@radix-ui/react-toast'
import { cva, type VariantProps } from 'class-variance-authority'
import { isEmpty } from 'lodash'
import { CircleCheck, Info, X } from 'lucide-react'
import { memo, useEffect, useState } from 'react'
import Button from '../Button'

const toastStyles = cva(
    'relative flex flex-col gap-2 w-[350px] bg-backgroundPrimary border-0.5 rounded-3 p-3 overflow-hidden data-[state=open]:animate-slideIn data-[state=closed]:animate-hide data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform data-[swipe=cancel]:duration-200 data-[swipe=cancel]:ease-out data-[swipe=end]:animate-swipeOut',
    {
        variants: {
            intent: {
                success: 'border-borderPrimary',
                error: 'border-borderNegativeSubdued',
                info: 'border-borderPrimary',
                warning: 'border-borderWarningSubdued',
                default: 'border-borderPrimary'
            }
        },
        defaultVariants: {
            intent: 'default'
        }
    }
)

const iconStyles = cva(
    'w-[24px] h-[24px] max-w-[24px] max-h-[24px] min-w-[24px] min-h-[24px] flex items-center justify-center rounded-full',
    {
        variants: {
            intent: {
                success: 'bg-backgroundPositiveLight',
                error: 'bg-backgroundNegativeLight',
                info: 'bg-backgroundInformationLight',
                warning: 'bg-backgroundWarningLight',
                default: 'bg-backgroundNeutralLight'
            }
        }
    }
)

const progressContainerStyles = cva('absolute bottom-0 left-0 right-0 h-[3px]', {
    variants: {
        intent: {
            success: 'bg-backgroundPositiveLight',
            error: 'bg-backgroundNegativeLight',
            info: 'bg-backgroundInformationLight',
            warning: 'bg-backgroundWarningLight',
            default: 'bg-borderPrimary'
        }
    }
})

const progressStyles = cva('h-[3px]', {
    variants: {
        intent: {
            success: 'bg-borderPositiveSubdued',
            error: 'bg-borderNegativeSubdued',
            info: 'bg-borderInformationSubdued',
            warning: 'bg-borderWarningSubdued',
            default: 'bg-borderTertiary'
        }
    }
})

const IconMap = {
    success: <Icon icon={CircleCheck} color="positive" />,
    error: <Icon icon={X} color="negative" />,
    info: <Icon icon={Info} color="information" />,
    warning: <Icon icon={Info} color="warning" />,
    default: <Icon icon={Info} color="primary" />
} as const

const resolveTitleColor = (intent: ToasterProps['intent']) => {
    if (intent === 'error') return 'negative'
    if (intent === 'warning') return 'warning'
    return 'black'
}

const CloseButton = memo(({ onClick }: { onClick: () => void }) => (
    <Toast.Close asChild>
        <button
            type="button"
            data-testid="__toaster-close-button__"
            className="unset absolute top-[8px] right-[8px]"
            onClick={onClick}
        >
            <Icon size="1" icon={X} color="primary" />
        </button>
    </Toast.Close>
))

// The top layer paints above every dialog and drawer, which no z-index can promise from inside the layout.
const showInTopLayer = (viewport: HTMLOListElement | null) => {
    viewport?.showPopover()
}

export type ToasterProps = VariantProps<typeof toastStyles> & {
    title: string
    description?: string
    action?: { label: string; onClick: () => void }
    isLoading?: boolean
    isClosable?: boolean
    persistent?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
    'data-testid'?: string
}

const Toaster = ({
    intent,
    title,
    description,
    action,
    isClosable = true,
    isLoading = true,
    persistent = false,
    open = false,
    onOpenChange,
    'data-testid': dataTestId
}: ToasterProps) => {
    const [progress, setProgress] = useState(100)

    useEffect(() => {
        let animationFrame: number

        if (open && !persistent) {
            const startTime = Date.now()
            const duration = 5_000

            const updateProgress = () => {
                const elapsed = Date.now() - startTime
                const remaining = Math.max(0, duration - elapsed)
                const progressValue = (remaining / duration) * 100

                if (progressValue > 0) {
                    setProgress(progressValue)
                    animationFrame = requestAnimationFrame(updateProgress)
                } else {
                    onOpenChange?.(false)
                }
            }

            animationFrame = requestAnimationFrame(updateProgress)
        }

        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame)
            }
        }
    }, [open, persistent, onOpenChange])

    return (
        <Toast.Provider swipeDirection="right">
            <Toast.Root
                data-testid={dataTestId}
                className={toastStyles({ intent })}
                open={open}
                onOpenChange={onOpenChange}
                duration={persistent ? Infinity : 10000}
            >
                {isClosable ? (
                    <CloseButton
                        onClick={() => {
                            onOpenChange?.(false)
                        }}
                    />
                ) : null}
                <div className="flex gap-4 items-center">
                    <div className={iconStyles({ intent })}>{IconMap[intent || 'default']}</div>
                    <div className="flex flex-col gap-[4px]">
                        <Toast.Title asChild>
                            <Paragraph
                                size={4}
                                variant="medium"
                                color={resolveTitleColor(intent)}
                                className="leading-[110%]"
                            >
                                {title}
                            </Paragraph>
                        </Toast.Title>
                        {isEmpty(description) ? null : (
                            <Toast.Description asChild>
                                <Paragraph data-testid="__toaster-description__" size={2}>
                                    {description}
                                </Paragraph>
                            </Toast.Description>
                        )}
                    </div>
                    {action ? (
                        <Button
                            data-testid="__toaster-action-button__"
                            size="small"
                            onClick={action.onClick}
                            intent="secondary"
                            className="w-fit"
                        >
                            {action.label}
                        </Button>
                    ) : null}
                </div>
                {!persistent && (
                    <div className={progressContainerStyles({ intent })}>
                        <div
                            data-testid="__toaster-progress-bar__"
                            className={progressStyles({ intent })}
                            style={{
                                width: isLoading ? `${progress}%` : '100%'
                            }}
                        />
                    </div>
                )}
            </Toast.Root>
            <Toast.Viewport
                ref={showInTopLayer}
                popover="manual"
                className="fixed inset-auto bottom-0 right-0 m-0 p-6 w-[400px] max-w-[100vw] list-none overflow-visible border-0 bg-transparent outline-hidden [--viewport-padding:24px]"
            />
        </Toast.Provider>
    )
}

export default Toaster
